// List variant (parked): `createContext`, `useContext` from 'react' and
// `LuChevronRight` from 'react-icons/lu'.
import { useRef, useState, type ReactNode } from 'react';
import type { IconType } from 'react-icons';
import { Link } from 'react-router';

import { useReducedMotion } from '@mds/shared/hooks/use-reduced-motion';
import { fileImageSrcSet } from '@mds/shared/lib/file-image';
import { cn } from '@mds/shared/lib/utils';
import { Skeleton } from '@mds/shared/ui/shadcn/skeleton';

/**
 * The rows every card lays itself out on, in order: picture, eyebrow, title,
 * description, meta, footer.
 *
 * Fixed at six whatever a given card actually carries. That is the whole
 * mechanism: each card spans the same six tracks of the row's grid, so track 3
 * is as tall as the tallest title in the row and every card's title sits in it.
 * A card with no eyebrow still spans track 2 — it just puts nothing in it, and
 * if no card in the row has an eyebrow the track is zero tall and costs
 * nothing.
 *
 * Exported because the cards line up inside `CardRail` as well, and a row and
 * a rail that disagree about how many tracks there are do not fail loudly —
 * they draw the cards a track out of step with each other.
 */
export const CARD_ROWS = 6;

/*
 * The list variant — parked. Below `sm` a card inside a `MediaCardRow` used to
 * turn into a list item: thumbnail left, words right, chevron at the end. It
 * is switched off so the card keeps its upright layout at every width and
 * only scales (see `PHONE` below). To bring it back, restore this block, the
 * `InCardRow` provider in `MediaCardRow`, `const list = useContext(InCardRow)`
 * in both components, and the `list && LIST.*` classes and the chevron marked
 * "list variant" below. The callers' `group-data-list:max-sm:*` classes are
 * still in place and come back on with `data-list`.
 *
 * const InCardRow = createContext(false);
 *
 * const LIST = {
 *   card: 'max-sm:mb-0 max-sm:grid-cols-[6.5rem_minmax(0,1fr)_auto] max-sm:grid-rows-[auto_auto_auto_auto_1fr] max-sm:gap-x-3 max-sm:p-3',
 *   bare: 'max-sm:rounded-card max-sm:border-b-0 max-sm:bg-surface max-sm:shadow-card',
 *   picture: 'max-sm:col-start-1 max-sm:row-span-5 max-sm:row-start-1 max-sm:p-0',
 *   frame: 'max-sm:size-26 max-sm:rounded-input',
 *   text: 'max-sm:col-start-2 max-sm:px-0 max-sm:pb-0',
 *   footer: 'max-sm:col-start-2',
 * };
 */

/**
 * The picture's shape: 4:3 on every screen, so a photo is cropped the same way
 * on a phone as on a desktop. Its height follows the card's width, and every
 * card in a row is the same width, so pictures still line up across the row.
 */
const FRAME = 'aspect-card';

/**
 * How wide the picture is drawn, for picking a display size: one card of a row
 * on a wide screen, the whole width on a phone, where a row is one card wide.
 */
const CARD_IMAGE_SIZES = '(min-width: 1024px) 33vw, 100vw';

/**
 * How the upright card steps down below `sm`, where a row is one card wide.
 * Nothing moves — the picture, the six tracks and their order are the same —
 * only the spacing tightens a step.
 */
const PHONE = {
  band: 'mb-8 sm:mb-12',
  pad: 'px-4 sm:px-5',
  foot: 'pb-4 sm:pb-5',
};

/**
 * One mono fact on a card — "6:00–8:00 PM", "20 seats".
 *
 * `icon` is the component itself rather than a rendered element, so the card
 * decides its size and colour; a caller passing `<LuClock />` would have baked
 * in its own. Same arrangement as `PackageCard`'s perks, for the same reason.
 *
 * A fact with no icon is a bare string, which is most of them: an icon earns
 * its place when it labels a *kind* of fact that repeats card to card, like a
 * date or a time, and is noise on a one-off like "Open floor".
 */
export type MediaCardFact = { label: string; icon?: IconType };

export type MediaCardProps = {
  /** Without one the frame stays, empty, so a row with a missing photo keeps its line. */
  image?: { src: string; alt: string };
  /**
   * Several photographs, swiped one at a time in the same 4:3 frame, with a
   * dot per photo laid over its foot. Takes the place of `image`.
   */
  images?: { src: string; alt: string }[];
  title: string;
  /**
   * `bare` is a picture with a caption under it, ruled off in mint, sitting
   * straight on the page — the spaces row.
   *
   * `framed` is a white card with a border and a lift on hover — the events
   * row. Same skeleton either way; the chrome is the only difference, which is
   * why these are one component and not two.
   */
  variant?: 'bare' | 'framed';
  /**
   * `framed` only: the card on the dark panel instead of white, for a band
   * that is a dark block on the page (the home page's booking band on a
   * phone). `data-ground="panel"` re-points the text roles, so the eyebrow,
   * description and meta take their dark-ground steps without classes of
   * their own.
   */
  dark?: boolean;
  /** Where the card leads. Without it the card is a panel rather than a link. */
  to?: string;
  /** The mono category above the title — "Open house". */
  eyebrow?: string;
  /** The line under the title: a space's rates, an event's blurb. */
  description?: string;
  /**
   * Shows the whole description instead of cutting it at three lines. For a
   * card that stands alone, like the home page's booking band on a phone: the
   * cap is there so a row of cards stays level, and a card on its own has no
   * row to keep level with.
   */
  fullDescription?: boolean;
  /**
   * Mono facts in a row — "6:00–8:00 PM", "Coworking floor". Separate entries
   * rather than one pre-joined line, so they wrap onto their own rows on a
   * narrow card instead of breaking mid-fact.
   *
   * A plain string is a fact with no icon; see `MediaCardFact` for one with.
   */
  meta?: (string | MediaCardFact)[];
  /** The pill over the image — a date, a status. */
  badge?: string;
  /**
   * Insets the picture, so the card's surface shows as a margin on all four
   * sides of it — a photograph mounted on the card rather than serving as its
   * lid.
   *
   * `framed` only: a bare card has no surface for the margin to be made of, so
   * on one this is ignored rather than producing a picture floating in a gap.
   */
  imageInset?: boolean;
  /**
   * The bottom row, as markup. A slot rather than a shape: the card owns the
   * track and lifts whatever lands in it above the stretched title link, and
   * the caller decides what a bottom row is — a price beside an action, a ruled
   * strip, nothing at all. Written inline at the call site, because what goes
   * in a bottom row is the page's business and not the card's.
   */
  footer?: ReactNode;
  className?: string;
};

/**
 * The row a set of `MediaCard`s sits in, and the reason they line up.
 *
 * It owns the six tracks the cards span. Without it — dropped into a plain
 * `flex` or a grid of its own — each card falls back to sizing itself, and a
 * row of them goes ragged: one card's title takes two lines and pushes its
 * description down while its neighbour's stays up.
 *
 * `gap-y-0` and a margin on the cards, rather than a row gap: the row gap would
 * apply between all six of a card's internal tracks as well as between bands of
 * cards, putting space inside every card that nothing asked for.
 */
/**
 * A `MediaCard` that has not arrived yet — for the cards still loading under
 * a list that is already on screen.
 *
 * It spans the same six tracks as a card and fills them the same way, so a
 * placeholder in a row with real cards lines up with them, and a page that
 * lands in its place does not move anything around it.
 */
export function MediaCardSkeleton({ variant = 'bare' }: { variant?: 'bare' | 'framed' }) {
  const framed = variant === 'framed';
  const pad = cn(framed && PHONE.pad);
  return (
    <div
      aria-hidden="true"
      className={cn(
        'row-span-6 grid grid-rows-subgrid gap-y-0',
        PHONE.band,
        framed ? 'overflow-hidden rounded-card border border-hairline bg-surface' : 'pb-4',
      )}
    >
      <div className={cn(framed && 'p-3')}>
        <Skeleton
          className={cn(FRAME, framed ? 'rounded-input' : 'rounded-card')}
        />
      </div>
      <div className={pad} />
      <div className={pad}>
        <Skeleton className={cn('h-5 w-3/4', framed ? 'mt-5' : 'mt-4')} />
      </div>
      <div className={pad}>
        <Skeleton className="mt-2 h-3 w-full" />
        <Skeleton className="mt-1.5 h-3 w-2/3" />
      </div>
      <div className={cn(pad, framed && PHONE.foot)}>
        <Skeleton className="mt-2 h-3 w-1/2" />
      </div>
      <div />
    </div>
  );
}

export function MediaCardRow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        // `auto-fill`, not `auto-fit`. They differ on exactly one thing: what
        // happens to the tracks nothing lands in. `auto-fit` collapses them and
        // shares the width out among the cards that exist, so filtering a row
        // of five down to one leaves that one card stretched across the whole
        // page — the same card, four times the width, for no reason the visitor
        // can see. `auto-fill` keeps the empty tracks, so a card is the width a
        // card is, however many of them the filter leaves.
        //
        // `min(18rem, 100%)` so a screen narrower than one card gets one card
        // as wide as the screen, rather than an 18rem card overflowing it.
        'grid grid-cols-[repeat(auto-fill,minmax(min(18rem,100%),1fr))] gap-x-8 gap-y-0',
        // Cancels the last row's band margin (`PHONE.band`), which has no row
        // under it to space from: what follows the row sets its own gap.
        '-mb-8 sm:-mb-12',
        // List variant (parked): 'max-sm:flex max-sm:flex-col max-sm:gap-3',
        className,
      )}
      // Six `auto` tracks. `auto` is what lets a track collapse to nothing when
      // no card in the row fills it, and grow to the tallest when one does.
      style={{ gridTemplateRows: `repeat(${CARD_ROWS}, auto)` }}
    >
      {/* List variant (parked): <InCardRow.Provider value>{children}</InCardRow.Provider> */}
      {children}
    </div>
  );
}

/**
 * A vertical card: picture on top, words underneath.
 *
 * Must be a direct child of `MediaCardRow`. It is a subgrid, so it has no
 * internal layout of its own to fall back on.
 *
 * `object-position: 50% 55%` on every image is from the design and is worth
 * keeping. These are photographs of rooms, where the interesting half is the
 * floor and the furniture; centring them crops to the ceiling.
 */
/**
 * Photographs swiped one at a time, filling the frame they are dropped into.
 *
 * A scroll-snap strip rather than a script carousel: the browser does the
 * swipe, its momentum and its snapping, and the dots only read where it landed
 * and scroll it on a tap. Lifted above the card's stretched title link, so a
 * swipe on a card with a `to` moves the photos rather than following the link.
 */
function PhotoSwipe({ images, label }: { images: { src: string; alt: string }[]; label: string }) {
  const track = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const reducedMotion = useReducedMotion();

  const show = (index: number) => {
    const el = track.current;
    if (!el) return;
    el.scrollTo({ left: index * el.clientWidth, behavior: reducedMotion ? 'auto' : 'smooth' });
  };

  return (
    <div role="group" aria-roledescription="carousel" aria-label={label} className="absolute inset-0 z-1">
      <div
        ref={track}
        onScroll={(e) => {
          const el = e.currentTarget;
          setActive(Math.round(el.scrollLeft / el.clientWidth));
        }}
        className={cn(
          'flex size-full snap-x snap-mandatory overflow-x-auto overscroll-x-contain',
          'scrollbar-none [&::-webkit-scrollbar]:hidden',
        )}
      >
        {images.map((photo, i) => (
          <img
            key={photo.src}
            src={photo.src}
            srcSet={fileImageSrcSet(photo.src)}
            sizes={CARD_IMAGE_SIZES}
            alt={photo.alt}
            loading={i === 0 ? 'eager' : 'lazy'}
            decoding="async"
            draggable={false}
            className="size-full flex-none snap-start object-cover object-[50%_55%]"
          />
        ))}
      </div>

      {/* A dot per photo, the shown one long. Real buttons for anyone who does
          not swipe, each a finger's worth to hit around the small dot. */}
      <div className="absolute inset-x-0 bottom-1 flex justify-center">
        {images.map((photo, i) => (
          <button
            key={photo.src}
            type="button"
            aria-label={`Show photo ${i + 1} of ${images.length}`}
            aria-pressed={i === active}
            onClick={() => show(i)}
            className="grid h-8 place-items-center px-1 focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-focus"
          >
            <span
              className={cn(
                'block h-1.5 rounded-full',
                'transition-[width,background-color] duration-(--duration-base) ease-standard motion-reduce:transition-none',
                i === active ? 'w-5 bg-surface' : 'w-1.5 bg-surface/55',
              )}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function MediaCard({
  image,
  images,
  title,
  to,
  eyebrow,
  description,
  fullDescription,
  meta,
  badge,
  imageInset,
  footer,
  variant = 'bare',
  dark,
  className,
}: MediaCardProps) {
  const framed = variant === 'framed';
  const onPanel = framed && dark;
  // List variant (parked): const list = useContext(InCardRow);

  // The props are one flat list, so `imageInset` can be passed to a bare card.
  // It means nothing there — the margin it asks for is made of the card's own
  // surface, and a bare card has none — so it is resolved against the variant
  // once, here, and every use below reads this rather than the raw prop.
  const inset = framed && imageInset;

  // Vertical rhythm has to live on the contents rather than on the cells,
  // because a cell with padding is a cell that is never zero tall — and a
  // track that cannot collapse defeats the point of the subgrid.
  const pad = cn(framed && PHONE.pad);

  // The gap above the first line of text, which is the eyebrow on a card that
  // has one and the title on a card that does not.
  //
  // One value per variant, never per card. Track 2 is shared, so a card that
  // sets a smaller margin here does not close a gap — it lifts its own eyebrow
  // out of line with every other eyebrow in the row while the track stays as
  // tall as the largest margin anyway. Differences between cards belong in the
  // picture track, which is sized to absorb them; see `imageHeight`.
  const firstTextSpacing = framed ? 'mt-5' : 'mt-4';

  return (
    <article
      data-ground={onPanel ? 'panel' : undefined}
      className={cn(
        // `row-span-6` with `grid-rows-subgrid` is the whole trick: the card
        // does not define rows, it borrows the row's.
        'group relative row-span-6 grid grid-rows-subgrid gap-y-0',
        // The band gap. A margin rather than the row's `gap-y`, which would
        // also land between the card's own six tracks.
        PHONE.band,
        framed
          ? [
              'overflow-hidden rounded-card border border-hairline',
              onPanel ? 'bg-panel text-on-panel' : 'bg-surface',
              'transition-[transform,box-shadow] duration-(--duration-base) ease-standard',
              'hover:-translate-y-1.5 hover:shadow-lift motion-reduce:transition-none',
            ]
          : // The rule sits on the card, not under the last line of text —
            // which card that is varies with what each one carries, and the
            // whole row's rules have to land on one line.
            //
            // `hairline`, the neutral divider, rather than a tint of the brand
            // edge: a row of mint rules under a row of photographs reads as
            // five underlined links, and the colour was doing no work that the
            // mono meta line above it was not already doing.
            'border-b border-hairline pb-4',
        // List variant (parked): list && [LIST.card, !framed && LIST.bare],
        className,
      )}
      // List variant (parked): lets a caller's footer restyle itself for the
      // list item with `group-data-list:max-sm:*`.
      // data-list={list || undefined}
    >
      {/* 1 — picture, 4:3 (`FRAME`). An inset picture is 4:3 inside its 12px
          mount, so its cell runs 6px taller than a flush one's; a row mixing
          the two carries those 6px under the flush photos. */}
      <div className={cn(inset && 'p-3')}>
        <div
          className={cn(
            'relative overflow-hidden bg-placeholder',
            FRAME,
            framed ? inset && 'rounded-input' : 'rounded-card',
          )}
        >
          {images && images.length > 0 ? (
            <PhotoSwipe images={images} label={title} />
          ) : image && (
            <img
              src={image.src}
              srcSet={fileImageSrcSet(image.src)}
              sizes={CARD_IMAGE_SIZES}
              alt={image.alt}
              loading="lazy"
              decoding="async"
              className={cn(
                'absolute inset-0 size-full object-cover object-[50%_55%]',
                'transition-transform duration-(--duration-slow) ease-standard',
                'group-hover:scale-105 motion-reduce:transition-none',
              )}
            />
          )}

          {badge && (
            <span
              className={cn(
                'absolute top-3 right-3 rounded-button bg-panel px-3.5 py-1.5',
                'font-mono text-2xs font-semibold text-brand',
                // List variant (parked): list && 'max-sm:hidden',
              )}
            >
              {badge}
            </span>
          )}
        </div>
      </div>

      {/* 2 — eyebrow */}
      <div className={pad}>
        {eyebrow && (
          // Small caps in muted ink, and left that way deliberately: the meta
          // row below it is the same size and face, and what separates the two
          // now is the icons down there rather than any chrome up here.
          // On the dark card it takes the accent instead, light so the bright
          // mint does not read as heavy: the band's own eyebrow, as the split.
          <span
            className={cn(
              'block font-mono text-2xs uppercase',
              onPanel ? 'font-light text-brand-text' : 'text-ink-muted',
              firstTextSpacing,
            )}
          >
            {eyebrow}
          </span>
        )}      </div>

      {/* 3 — title. Clamped at two lines, but never *reserving* two: the track
          is one line tall until some card in the row needs a second, and then
          every card gets it. */}
      <div className={pad}>
        <h3
          className={cn(
            'line-clamp-2 tracking-tight',
            // Steps down on a phone from the token, rail and list alike.
            framed ? 'text-heading-md' : 'text-heading-md font-medium',
            // List variant (parked): `max-sm:mt-1` under an eyebrow.
            eyebrow ? 'mt-2' : firstTextSpacing,
          )}
        >
          {to ? (
            // Stretched over the whole card with `after:inset-0`, so the
            // picture is clickable without being a second link to the same
            // place. Two links with one destination is a tab stop and an
            // announcement for nothing.
            <Link
              to={to}
              className={cn(
                'rounded-sm after:absolute after:inset-0',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
              )}
            >
              {title}
            </Link>
          ) : (
            title
          )}
        </h3>
      </div>

      {/* 4 — description */}
      <div className={pad}>
        {description && (
          // List variant (parked): left off the list item with `max-sm:hidden`.
          <p
            className={cn(
              'mt-2 text-xs leading-normal text-ink-muted',
              !fullDescription && 'line-clamp-3',
            )}
          >
            {description}
          </p>
        )}
      </div>

      {/* 5 — meta. `font-medium`: at 11px in the mono face and in mint, the
          regular weight read thin and faint; one step up holds the facts
          without turning them bold. */}
      <div className={cn(pad, framed && PHONE.foot)}>
        {meta && (
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5 group-data-list:max-sm:mt-1 font-mono text-2xs font-medium text-brand-text uppercase">
            {meta.map((fact) => {
              const { label, icon: Icon } =
                typeof fact === 'string' ? { label: fact, icon: undefined } : fact;

              return (
                <span key={label} className="inline-flex items-center gap-1.5">
                  {/* Decorative: the date beside it says the same thing in
                      words, so announcing the glyph as well would read the
                      fact twice. */}
                  {Icon && <Icon aria-hidden="true" className="size-3 shrink-0" />}
                  {label}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* 6 — footer. Full-bleed on purpose: the card contributes the track and
          nothing else, so the slot can hold a strip that rules across the whole
          width as readily as a row inset to `px-5`.

          `relative` is the card's job rather than the caller's. It lifts the
          slot above the title link stretched over the card by `after:inset-0`,
          which would otherwise cover anything in here and swallow the click.

          Lifting it is what makes the pointer rule necessary. Raised over that
          link, the slot takes the hover and the click for the whole bottom of
          the card — so on a card with a `to`, the footer alone showed a plain
          arrow and did nothing while every other part of the card showed a
          pointer and navigated. One card, two behaviours, on a boundary the
          visitor cannot see.

          So the slot is transparent to the pointer and real controls opt back
          in. A caption, a price, a "Details ›" cue — anything that is not a
          link or a button — hands the hover and the click down to the card's
          own link, and the cursor is the same everywhere on the card. */}
      <div
        className={cn(
          'relative self-end',
          // List variant (parked): list && LIST.footer,
          to && 'pointer-events-none [&_a]:pointer-events-auto [&_button]:pointer-events-auto',
        )}
      >
        {footer}
      </div>

      {/* List variant (parked): the list item's chevron, its "this goes
          somewhere" in place of the footer's words.

      {list && to && (
        <LuChevronRight
          aria-hidden="true"
          className="hidden size-4 self-center text-ink-muted max-sm:col-start-3 max-sm:row-span-5 max-sm:row-start-1 max-sm:block"
        />
      )} */}
    </article>
  );
}

